import { create } from 'zustand';
import { useAuthStore as useCoreAuthStore } from '@flows.cash/core';
import { toast } from 'sonner';
import { t } from 'i18next';
import { authService } from '@/services/authService';
import { supabase } from '@/lib/supabase';

// Re-export the core auth store with added toast notifications
export const useAuthStore = create(() => {
  const coreStore = useCoreAuthStore.getState();
  
  // Subscribe to the core store to update our store when it changes
  useCoreAuthStore.subscribe((state) => {
    useAuthStore.setState({
      user: state.user,
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading
    });
  });
  
  return {
    // Expose the core store's state
    user: coreStore.user,
    isAuthenticated: coreStore.isAuthenticated,
    isLoading: coreStore.isLoading,
    
    // Override methods to add toast notifications
    login: async (email: string, password: string) => {
      try {
        await coreStore.login(email, password);
        toast.success('Successfully logged in');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to login');
        throw error;
      }
    },
    
    register: async (email: string, password: string, displayName: string) => {
      try {
        await coreStore.register(email, password, displayName);
        toast.success('Account created successfully');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to create account');
        throw error;
      }
    },
    
    logout: async (isUserSwitching = false) => {
      try {
        // First check if there's actually a session to prevent the "Auth session missing!" error
        const { data } = await supabase.auth.getSession();
        
        if (!data.session) {
          // Call reset directly to clear the store state without calling the API
          coreStore.reset();
          toast.success('Successfully logged out');
          return;
        }
        
        // Proceed with normal logout if we have a session
        await coreStore.logout(isUserSwitching);
        toast.success('Successfully logged out');
      } catch (error) {
        // Even if logout fails, we should still clear the local state
        coreStore.reset();
        toast.error(error instanceof Error ? error.message : 'Failed to logout');
        // Don't rethrow the error for logout to allow UI to continue
      }
    },
    
    resetPassword: async (email: string) => {
      try {
        const result = await authService.resetPassword(email);
        
        if (result.success) {
          toast.success(t('auth.resetPasswordSuccess'));
          return { success: true };
        } else {
          toast.error(t('auth.resetPasswordError'));
          return { success: false, error: result.error };
        }
      } catch (error) {
        toast.error(t('auth.resetPasswordError'));
        return { success: false, error: error instanceof Error ? error : new Error('Unknown error') };
      }
    },
    
    loginWithGoogle: async () => {
      try {
        await coreStore.loginWithGoogle();
        // For OAuth, we don't show success toast here since it will redirect
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to login with Google');
        throw error;
      }
    },
    
    loginWithApple: async () => {
      try {
        await coreStore.loginWithApple();
        // For OAuth, we don't show success toast here since it will redirect
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to login with Apple');
        throw error;
      }
    }
  };
});